import * as React from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Zap, Timer, RotateCcw, LifeBuoy, AlignLeft,
  Trophy, Flame, ChevronRight, Gift, Target, Layers, ArrowRight, CheckCircle2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { CountUp } from "@/components/effects/count-up";
import { BlurReveal } from "@/components/effects/blur-reveal";
import { DashboardBackground } from "@/components/dashboard-background";
import { ContainerTextFlip } from "@/components/aceternity/container-text-flip";
import { TypewriterEffect } from "@/components/aceternity/typewriter-effect";
import { PageFrame } from "@/components/page-frame";
import { AppTopBar } from "@/components/app-topbar";
import { PfpAvatar } from "@/components/pfp-avatar";
import { useAuth } from "@/lib/auth-context";
import {
  fetchLeaderboard, fetchTopics, fetchTopicMastery,
  resolveStartTopic, getRecentTopicId, setRecentTopicId,
  type LeaderboardRow, type Topic, type TopicMastery
} from "@/lib/queries";

export default function Dashboard() {
  const { user, access } = useAuth();
  const [board, setBoard]   = React.useState<LeaderboardRow[]>([]);
  const [topics, setTopics] = React.useState<Topic[]>([]);
  const [mastery, setMastery] = React.useState<Map<string, TopicMastery>>(new Map());
  const [currentTopic, setCurrentTopic] = React.useState<Topic | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const [b, t, m] = await Promise.all([
        fetchLeaderboard(50),
        fetchTopics(),
        fetchTopicMastery()
      ]);
      if (cancelled) return;
      setBoard(b);
      setTopics(t);
      setMastery(m);
      const cur = await resolveStartTopic(t, m);
      if (cancelled) return;
      if (cur) setCurrentTopic(cur);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const profile = access?.profile;
  const fullName = profile?.full_name || (user?.email?.split("@")[0] ?? "you");
  const firstName = fullName.split(" ")[0];
  const myStreak = board.find(r => r.id === user?.id)?.streak ?? 0;
  const points = profile?.total_points ?? 0;

  const topTen = React.useMemo(() => {
    return [...board]
      .filter(r => r.total_points > 0)
      .sort((a, b) => b.total_points - a.total_points)
      .slice(0, 10);
  }, [board]);

  // Overall course progress = average pct across topics with vocab.
  const overall = React.useMemo(() => {
    const arr = Array.from(mastery.values()).filter(t => t.total > 0);
    if (arr.length === 0) return { pct: 0, correct: 0, total: 0 };
    const correct = arr.reduce((s, t) => s + t.correct, 0);
    const total   = arr.reduce((s, t) => s + t.total, 0);
    return {
      pct:     total > 0 ? Math.round((correct / total) * 100) : 0,
      correct, total
    };
  }, [mastery]);

  const recentId = getRecentTopicId();
  const currentTopicForLinks = currentTopic?.id ?? topics[0]?.id ?? "";

  function continueWith(modePath: string) {
    if (currentTopic) setRecentTopicId(currentTopic.id);
    return currentTopicForLinks
      ? `${modePath}?topic=${encodeURIComponent(currentTopicForLinks)}`
      : modePath;
  }

  return (
    <PageFrame surface="app">
      <AppTopBar />
      <DashboardBackground />
      <div className="relative">
        <div className="relative mx-auto max-w-7xl px-4 md:px-6 pt-8 pb-32">
          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            <div className="space-y-6">

              {/* Welcome */}
              <BlurReveal>
                <section className="rounded-3xl border border-line bg-bg-2/70 backdrop-blur-sm p-6 md:p-8 relative overflow-hidden">
                  <div aria-hidden className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-accent/15 blur-3xl" />
                  <div aria-hidden className="absolute -left-16 bottom-0 w-56 h-56 rounded-full bg-accent/8 blur-3xl" />
                  <div className="relative grid md:grid-cols-[auto_1fr] gap-6 items-center">
                    <CourseBadge />
                    <div>
                      <div className="text-xs uppercase tracking-widest text-fg-3 mb-2">Fuṣḥā · Arabic GCSE</div>

                      <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <ContainerTextFlip
                          words={[
                            "Welcome back",
                            "Ready to learn",
                            "Keep going",
                            "Yallah"
                          ]}
                          interval={2800}
                          className="text-fg text-xl md:text-2xl"
                        />
                        <span>,</span>
                        <span className="text-accent">{firstName}.</span>
                      </h1>

                      <div className="mt-3 text-sm">
                        {currentTopic ? (
                          <TypewriterEffect
                            key={currentTopic.id}
                            speed={32}
                            words={[
                              { text: "You're", className: "text-fg-2" },
                              { text: "on",     className: "text-fg-2" },
                              { text: currentTopic.name_en + ".", className: "text-fg font-semibold" },
                              { text: "Pick",   className: "text-fg-2" },
                              { text: "a",      className: "text-fg-2" },
                              { text: "mode",   className: "text-fg-2" },
                              { text: "below.", className: "text-fg-2" }
                            ]}
                            cursorClassName="bg-accent"
                          />
                        ) : (
                          <span className="text-fg-2">Pick a mode below to keep your streak alive.</span>
                        )}
                      </div>

                      <div className="mt-5 flex items-center gap-3">
                        <span className="text-3xl font-bold text-accent drop-shadow-[0_0_12px_rgba(201,169,106,0.4)]">
                          <CountUp to={points} />
                        </span>
                        <span className="text-xs uppercase tracking-widest text-fg-3">points</span>
                        <Link to="/leaderboard" className="ml-auto text-xs text-fg-2 hover:text-fg inline-flex items-center gap-1">
                          View leaderboard <ChevronRight className="w-3 h-3" />
                        </Link>
                      </div>

                      {/* Overall course progress */}
                      <div className="mt-4">
                        <div className="flex items-baseline gap-2 text-xs text-fg-3 mb-1">
                          <span>Overall course progress</span>
                          <span className="text-fg-2 font-semibold ml-auto">{overall.correct} / {overall.total}</span>
                          <span className="text-accent font-bold">{overall.pct}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-bg overflow-hidden">
                          <div className="h-full bg-accent transition-all duration-700 shadow-[0_0_8px_rgba(201,169,106,0.6)]" style={{ width: `${overall.pct}%` }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </BlurReveal>

              {/* Daily reward / continue strip — rotating rainbow border */}
              <BlurReveal delay={0.05}>
                <section className="rainbow-border-strip relative overflow-hidden rounded-2xl p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-4 bg-bg-2/85 backdrop-blur-sm">
                  <div aria-hidden className="pointer-events-none absolute -top-12 -right-12 w-44 h-44 rounded-full bg-accent/15 blur-3xl" />
                  <div className="relative inline-flex items-center justify-center w-12 h-12 rounded-xl bg-accent/20 border border-accent-lo text-accent shrink-0">
                    <Gift className="w-6 h-6" />
                  </div>
                  <div className="relative flex-1">
                    <div className="font-semibold">{currentTopic ? `Continue with ${currentTopic.name_en}` : "Daily reward"}</div>
                    <div className="text-sm text-fg-2">Run any mode today to keep your streak going.</div>
                  </div>
                  <Button size="md" className="relative md:ml-auto" asChild>
                    <Link to={continueWith("/learn")}>Continue <ChevronRight className="w-4 h-4" /></Link>
                  </Button>

                  <style>{`
                    .rainbow-border-strip { isolation: isolate; }
                    .rainbow-border-strip::before {
                      content: ""; position: absolute; inset: -2px;
                      border-radius: inherit; padding: 2px; z-index: -1;
                      background: conic-gradient(from 0deg,
                        #e0c388 0%, #d97757 18%, #a78bfa 36%,
                        #38bdf8 54%, #6ad080 72%, #f9d56e 88%, #e0c388 100%);
                      -webkit-mask: linear-gradient(#000,#000) content-box, linear-gradient(#000,#000);
                      -webkit-mask-composite: xor; mask-composite: exclude;
                      animation: rainbow-strip-spin 5s linear infinite;
                      opacity: .9;
                    }
                    @keyframes rainbow-strip-spin { to { transform: rotate(360deg); } }
                    @supports not (background: conic-gradient(red, blue)) {
                      .rainbow-border-strip::before { background: #c9a96a; animation: none; }
                    }
                    @media (prefers-reduced-motion: reduce) {
                      .rainbow-border-strip::before { animation: none; }
                    }
                  `}</style>
                </section>
              </BlurReveal>

              {/* Modes — at top, all working ones first */}
              <BlurReveal delay={0.1}>
                <section>
                  <h2 className="text-xs uppercase tracking-widest text-fg-3 mb-3">Study modes</h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {MODES.map(m => (
                      <Link key={m.title} to={continueWith(m.to)} className="block group">
                        <Card className={
                          "transition-all relative overflow-hidden " +
                          (m.live
                            ? "hover:border-line-2 hover:bg-bg-3/60"
                            : "opacity-80 hover:opacity-100")
                        }>
                          {!m.live && (
                            <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest text-fg-3">Soon</span>
                          )}
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-accent/10 border border-accent-lo text-accent group-hover:scale-105 transition-transform">
                                <m.icon className="w-5 h-5" />
                              </div>
                              <ChevronRight className="w-4 h-4 text-fg-3 group-hover:text-fg group-hover:translate-x-0.5 transition-all" />
                            </div>
                            <CardTitle className="mt-3">{m.title}</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm text-fg-2">{m.sub}</p>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </section>
              </BlurReveal>

              {/* Topics — with real progress bars */}
              <BlurReveal delay={0.15}>
                <section>
                  <div className="flex items-baseline justify-between mb-3">
                    <h2 className="text-xs uppercase tracking-widest text-fg-3">Topics</h2>
                    {topics.length > 6 && (
                      <Link to="/learn" className="text-xs text-fg-2 hover:text-fg inline-flex items-center gap-1">
                        View all {topics.length} <ChevronRight className="w-3 h-3" />
                      </Link>
                    )}
                  </div>

                  {loading ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="h-28 rounded-2xl bg-bg-2/40 animate-pulse" />
                      ))}
                    </div>
                  ) : topics.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-line bg-bg-2/40 p-8 text-center text-sm text-fg-2">
                      No topics yet — check back soon.
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {topics.slice(0, 9).map((t, i) => (
                        <TopicCard
                          key={t.id}
                          number={i + 1}
                          topic={t}
                          mastery={mastery.get(t.id)}
                          isCurrent={t.id === recentId || t.id === currentTopic?.id}
                          onPick={() => setRecentTopicId(t.id)}
                        />
                      ))}
                    </div>
                  )}

                  <div className="mt-6 flex justify-center">
                    <Button variant="outline" size="lg" asChild>
                      <Link to="/learn">
                        <Layers className="w-4 h-4" /> View all levels
                        {topics.length > 0 && <span className="text-fg-3 ml-1">({topics.length})</span>}
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </Button>
                  </div>
                </section>
              </BlurReveal>
            </div>

            {/* ── Sidebar ──────────────────────────────────────── */}
            <aside className="lg:sticky lg:top-24 lg:self-start space-y-6">
              <BlurReveal delay={0.2}>
                <section className="rounded-3xl border border-line bg-bg-2/70 backdrop-blur-sm p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Trophy className="w-4 h-4 text-accent" />
                    <h2 className="text-sm font-semibold">Leaderboard</h2>
                    <Badge variant="outline" className="ml-auto text-[10px]">Top 10</Badge>
                  </div>

                  {loading ? (
                    <ul className="space-y-1.5">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <li key={i} className="h-10 rounded-lg bg-bg-3/40 animate-pulse" />
                      ))}
                    </ul>
                  ) : topTen.length === 0 ? (
                    <p className="text-sm text-fg-2 px-1 py-2">No one's scored yet. Run a round and you'll be on top.</p>
                  ) : (
                    <ol className="space-y-1.5">
                      {topTen.map((l, i) => {
                        const me = l.id === user?.id;
                        return (
                          <li key={l.id} className={
                            "flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg " +
                            (me ? "bg-accent/10 border border-accent-lo" : "hover:bg-bg-3")
                          }>
                            <span className={
                              "inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold shrink-0 " +
                              (i === 0 ? "bg-yellow-400/15 text-yellow-300"
                                : i === 1 ? "bg-slate-300/15 text-slate-200"
                                : i === 2 ? "bg-amber-700/20 text-amber-400"
                                : "bg-bg text-fg-3 border border-line")
                            }>{i + 1}</span>
                            <PfpAvatar size="xs" url={l.avatar_url} name={l.full_name} seed={l.id} />
                            <span className={"flex-1 truncate text-sm " + (me ? "font-semibold text-fg" : "text-fg-2")}>
                              {l.full_name}{me && <span className="text-fg-3 text-xs"> · you</span>}
                            </span>
                            <span className="text-xs font-bold text-accent">{l.total_points.toLocaleString()}</span>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                  <Separator className="my-4" />
                  <Link to="/leaderboard" className="block text-center text-xs text-fg-2 hover:text-fg">
                    View full leaderboard →
                  </Link>
                </section>
              </BlurReveal>

              <BlurReveal delay={0.25}>
                <section className="rounded-3xl border border-line bg-bg-2/70 backdrop-blur-sm p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Flame className={"w-4 h-4 " + (myStreak > 0 ? "text-accent" : "text-fg-3")} />
                    <h2 className="text-sm font-semibold">Your streak</h2>
                  </div>
                  <div className="text-3xl font-bold tracking-tight">
                    <CountUp to={myStreak} /> <span className="text-base font-normal text-fg-2">{myStreak === 1 ? "day" : "days"}</span>
                  </div>
                  <p className="mt-2 text-xs text-fg-3">
                    {myStreak === 0
                      ? "Start a streak today — one mode keeps it lit."
                      : "Hit any mode today to keep it alive."}
                  </p>
                  <Link to="/streak" className="mt-3 inline-flex items-center gap-1 text-xs text-fg-2 hover:text-fg">
                    See full history <ChevronRight className="w-3 h-3" />
                  </Link>
                </section>
              </BlurReveal>

              <BlurReveal delay={0.3}>
                <section className="rounded-3xl border border-line bg-bg-2/70 backdrop-blur-sm p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-4 h-4 text-accent" />
                    <h2 className="text-sm font-semibold">Today's goal</h2>
                  </div>
                  <div className="text-3xl font-bold tracking-tight">500</div>
                  <div className="text-xs text-fg-3 mb-3">points · 5 min</div>
                  <div className="h-2 rounded-full bg-bg overflow-hidden">
                    <div className="h-full bg-accent" style={{ width: "0%" }} />
                  </div>
                  <p className="mt-3 text-xs text-fg-2">A 10-question quiz gets you most of the way.</p>
                </section>
              </BlurReveal>
            </aside>
          </div>
        </div>
      </div>
    </PageFrame>
  );
}

function TopicCard({ number, topic, mastery, isCurrent, onPick }: {
  number: number;
  topic: Topic;
  mastery?: TopicMastery;
  isCurrent: boolean;
  onPick: () => void;
}) {
  const pct = mastery?.pct ?? 0;
  const isDone = pct >= 80;
  return (
    <Link
      to={`/learn?topic=${encodeURIComponent(topic.id)}`}
      onClick={onPick}
      className={
        "rounded-2xl border bg-bg-2/70 backdrop-blur-sm p-4 hover:border-line-2 hover:bg-bg-3 transition-colors group flex flex-col " +
        (isCurrent ? "border-accent ring-1 ring-accent/40"
          : isDone ? "border-ok/40"
          : "border-line")
      }>
      {/* Header row: number + status icons + chevron */}
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center justify-center min-w-[2rem] h-7 px-2 rounded-lg bg-bg border border-line text-xs font-bold text-fg-2 tabular-nums shrink-0">
          {number}
        </span>
        <div className="flex-1 flex items-center gap-1.5 min-w-0">
          {isCurrent && <Badge variant="accent" className="text-[9px] px-1.5 py-0.5">Current</Badge>}
          {isDone && !isCurrent && <CheckCircle2 className="w-3.5 h-3.5 text-ok shrink-0" />}
        </div>
        <ChevronRight className="w-4 h-4 text-fg-3 group-hover:text-fg transition-colors shrink-0" />
      </div>

      {/* Arabic — full width, RTL, allowed to wrap. */}
      <div className="text-2xl md:text-[1.7rem] leading-tight text-accent text-right break-words"
           dir="rtl"
           style={{ fontFamily: "'Noto Sans Arabic',serif" }}>
        {topic.name_ar}
      </div>

      {/* English subtitle */}
      <div className="mt-1.5 text-sm font-semibold text-fg truncate">{topic.name_en}</div>
      <div className="text-xs text-fg-3">{mastery?.total || 0} words · {mastery?.correct || 0} mastered</div>

      {/* Progress bar — pinned at the bottom by flex-col mt-auto */}
      <div className="mt-auto pt-3 flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-bg overflow-hidden">
          <div
            className={"h-full transition-all duration-500 " + (isDone ? "bg-ok" : "bg-accent")}
            style={{ width: `${pct}%` }} />
        </div>
        <span className="text-[10px] uppercase tracking-widest text-fg-3 tabular-nums">{pct}%</span>
      </div>
    </Link>
  );
}

function CourseBadge() {
  return (
    <div className="relative inline-flex items-center justify-center w-32 h-32 rounded-3xl border border-accent-lo bg-gradient-to-br from-accent/20 via-accent/5 to-transparent">
      <div className="absolute inset-2 rounded-2xl border border-accent-lo/40" />
      <div className="text-center text-accent">
        <div className="text-[10px] uppercase tracking-widest opacity-80">Fuṣḥā</div>
        <div className="text-2xl font-bold mt-1" style={{ fontFamily: "'Noto Sans Arabic',serif" }}>العربيّة</div>
        <div className="text-[10px] uppercase tracking-widest opacity-80 mt-1">Course</div>
      </div>
    </div>
  );
}

const MODES = [
  { to: "/learn",            icon: Sparkles,  title: "Learn",            sub: "Flashcards + mini-test on your current topic", live: true },
  { to: "/quiz",             icon: Zap,       title: "Quiz",             sub: "10 MCQs · English ↔ Arabic",                   live: true },
  { to: "/sentence-builder", icon: AlignLeft, title: "Sentence builder", sub: "Drag Arabic into the right order",             live: true },
  { to: "/speed-review",     icon: Timer,     title: "Speed review",     sub: "8s per question · up to 15 pts each",          live: true },
  { to: "/revise",           icon: RotateCcw, title: "Revise",           sub: "Flashcards over every word you've practised",  live: true },
  { to: "/assistance",       icon: LifeBuoy,  title: "Assistance",       sub: "Recover the words you got wrong",              live: true }
];
